import { useState, useEffect } from 'react';
import { cn } from '../../utils/cn';
import type { NavigationProps } from '../../types/navigation';
import { Icon } from '../ui';
import { MobileMenu } from './index';
import { scrollToSection } from '../../utils/scroll';
import { useScrollPosition } from '../../hooks';

export function Navigation({ activeSection, sections, className }: NavigationProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const scrollPosition = useScrollPosition({ throttle: 50 });
  
  // Show navigation when scrolled past hero section
  const isScrolled = scrollPosition.y > 100;

  // Handle smooth scroll to section
  const handleSectionClick = (sectionId: string) => {
    scrollToSection(sectionId, 80); // 80px offset for sticky nav
    setIsMobileMenuOpen(false);
  };

  // Handle mobile menu toggle
  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          {
            'bg-white/95 backdrop-blur-sm shadow-sm': isScrolled,
            'bg-transparent': !isScrolled,
            'translate-y-0': isScrolled,
            '-translate-y-full': !isScrolled && activeSection !== 'hero'
          },
          className
        )}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={cn(
              'flex items-center justify-between transition-all duration-300',
              isScrolled ? 'h-14 sm:h-16' : 'h-28 sm:h-32'
            )}
          >
            {/* Logo */}
            <div className="flex-shrink-0">
              <button
                onClick={() => handleSectionClick('hero')}
                className="block focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2 rounded-sm"
                aria-label="Go to homepage"
              >
                <img
                  src="/images/vc-law-logo.jpg"
                  alt="VC Law Associates"
                  className={cn(
                    'w-auto transition-all duration-300',
                    isScrolled ? 'h-10 sm:h-12' : 'h-20 sm:h-24'
                  )}
                />
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-6">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => handleSectionClick(section.id)}
                    className={cn(
                      'px-3 py-2 text-sm font-medium transition-colors duration-200 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2',
                      {
                        'text-accent-gold border-b-2 border-accent-gold': activeSection === section.id,
                        'text-primary-navy hover:text-accent-gold': activeSection !== section.id
                      }
                    )}
                    aria-current={activeSection === section.id ? 'page' : undefined}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={handleMobileMenuToggle}
                className="inline-flex items-center justify-center p-2 rounded-md text-primary-navy hover:text-accent-gold hover:bg-background-light focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2 transition-colors duration-200"
                aria-expanded={isMobileMenuOpen}
                aria-label="Toggle mobile menu"
              >
                <Icon 
                  name={isMobileMenuOpen ? 'close' : 'menu'} 
                  size="md" 
                  className="transition-transform duration-200"
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onToggle={handleMobileMenuToggle}
        sections={sections}
        activeSection={activeSection}
        onSectionClick={handleSectionClick}
      />
    </>
  );
}